import { Box, Link as MuiLink } from '@mui/material';
import DefaultText from '../../components/Accessibility/DefaultText';
import DefaultTextBold from '../../components/Accessibility/DefaultTextBold';
import PageTitle from '../../components/Accessibility/PageTitle';
import { useAccessibility } from '../../Store/contexts/AccessibilityContext';
import './styles.css';
import { useEffect, useState } from 'react';
import { getMensagemRestricao, getMenuMapaDoSite } from '../../services/Home/HomeService';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import LoadingApp from '../../components/LoadingApp';
import { sxSubTitle, sxTextLink } from './sxProps';
import ModalFeedBack from '../../components/ModalFeedBack';
import { MenuMapaItem, SubMenuMapaItem } from '../../interfaces/entities/MapaDoSite';
import { getAgendamentosTeleatendimento } from '../../services/AgendamentoService';
import { ModalFeedBackProps } from '../../interfaces/props/ModalFeedBackProps';


const MapaDoSite = () => {
    const navigate = useNavigate();
    const { calculateTextStyle } = useAccessibility();
    const [loading, setLoading] = useState<boolean>(false);
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [menu, setMenu] = useState<MenuMapaItem[]>([]);
    const [showModalRestricted, setShowModalRestricted] = useState<boolean>(false);
    const [modalText, setModalText] = useState<string>('');
    const [showLoading, setShowLoading] = useState<boolean>(false);
    const [modalFeedback, setModalFeedback] = useState<null | ModalFeedBackProps>(null);

    const handleRestrictedAccess = (component: string) => {
        setLoading(true);
        getMensagemRestricao(component)
            .then(response => {
                setModalText(response.data)
            })
            .catch(error => {
                console.log(error);
                setModalText('Acesso não permitido.')
            })
            .finally(() => {
                setLoading(false);
                setShowModalRestricted(true);
            })
    }


    useEffect(() => {
        setLoading(true);
        getMenuMapaDoSite()
            .then((response: MenuMapaItem[]) => {
                setMenu(response);
            })
            .catch(() => {
                setShowErrorModal(true);
            })
            .finally(() => setLoading(false));
    }, []);

    const showFeedbackModal = (config: ModalFeedBackProps) => {
        setModalFeedback({
            ...config,
            handleShowModal: config.handleShowModal || (() => setModalFeedback(null)),
            handleButtonModal: config.handleButtonModal || (() => {
                setModalFeedback(null);
            }),
        });
    };

    const handleClickIngressarNoAtendimento = async () => {
        setShowLoading(true);
        const response = await getAgendamentosTeleatendimento();
        if (response?.status === 200) {
            const availableMeetings = response.data;
            const meeting = availableMeetings.at(0);
            const telemedicinaReady = meeting?.statusTelemedicina === 'disponivel' && meeting.statusTelemedicinaAPI === 'Iniciada';
            if (meeting && telemedicinaReady) {
                const isPaVirtual = meeting.tipoAtend === 'PA VIRTUAL';
                navigate(`/saudeOnline/ingressarNoAtendimento/${isPaVirtual ? `${meeting.id}/paVirtual` : `${meeting.idReuniao}`}`);
            } else {
                const emptySchedulesMessage = 'Você não possui nenhum agendamento confirmado por telessaúde. Caso já tenha realizado um agendamento, acesse "Consultar meus agendamentos" para conferir o status.';
                showFeedbackModal({
                    icon: "warning",
                    title: "Nenhum atendimento",
                    subTitle: emptySchedulesMessage,
                    textButton: "Fechar",
                    showButtonModal: true,
                    showModal: true
                });
            }
        } else {
            showFeedbackModal({
                icon: "error",
                title: "Navegando pelo teclado",
                subTitle: 'Ocorreu um erro ao fazer a consulta de teleatendimentos. Tente novamente em instantes.',
                textButton: "Fechar",
                showButtonModal: true,
                showModal: true
            });
        }
        setShowLoading(false);
    }

    const renderSubMenu = (subMenu: SubMenuMapaItem[]) => {
        return subMenu?.map((subItem) => (
            <Box
                key={subItem.title}
                className="mapa-list-subitems"
            >
                {subItem?.subMenu && subItem?.subMenu?.length > 0 ? (
                    <DefaultText
                        text={subItem?.title}
                        sx={sxSubTitle}
                    />
                ) : subItem?.url ? (
                    subItem.restricaoDeAcesso ? (
                        <Box className="mapa-subtitle-restricted" onClick={() => handleRestrictedAccess(subItem?.component ?? '')}>
                            <DefaultText text={subItem.title} sx={sxTextLink} />
                        </Box>
                    ) : (
                        <MuiLink
                            href={subItem?.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mapa-subtitle-link"
                            sx={sxTextLink}
                        >
                            <DefaultText text={subItem.title} sx={sxTextLink} />
                        </MuiLink>
                    )
                ) : subItem?.component ? (
                    subItem.restricaoDeAcesso ? (
                        <Box className="mapa-restricted" onClick={() => handleRestrictedAccess(subItem?.component ?? '')}>
                            <DefaultText text={subItem.title} sx={sxTextLink} />
                        </Box>
                    ) : (
                        <RouterLink to={`/${subItem.component}`} className="mapa-subtitle-link">
                            <DefaultText text={subItem.title} sx={sxTextLink} />
                        </RouterLink>
                    )
                ) : (
                    <DefaultText text={subItem.title} sx={sxTextLink} />
                )}

                {subItem?.subMenu && subItem?.subMenu.length > 0 && (
                    <Box className="mapa-list-submenu" sx={{ marginTop: '8px' }}>
                        {renderSubMenu(subItem.subMenu)}
                    </Box>
                )}
            </Box>
        ));
    };


    return (
        <div className="page-container">
            <LoadingApp showLoading={loading || showLoading} text="Carregando..." />
            <PageTitle text="Mapa do site" />
            {menu.map((item) => (
                <div key={item.title} className="mapa-list-submenu">
                    {item.subMenu && item.subMenu.length > 0 ? (
                        <DefaultTextBold
                            text={item.title}
                            style={{ ...calculateTextStyle(20) }}
                            className="mapa-title"
                        />
                    ) : item.component ? (
                        item.restricaoDeAcesso ? (
                            <Box className="mapa-title-restricted" onClick={() => handleRestrictedAccess(item.component ?? '')}>
                                <DefaultTextBold
                                    text={item.title}
                                    style={{ ...calculateTextStyle(20) }}
                                    sx={sxTextLink} />
                            </Box>
                        ) : (
                            item.component === 'ingressarAtendimento' ?
                                <Box className="mapa-subtitle-link" onClick={() => handleClickIngressarNoAtendimento()}>
                                    <DefaultTextBold
                                        text={item.title}
                                        style={{ ...calculateTextStyle(20) }}
                                        sx={sxTextLink} />
                                </Box>
                                :
                                <RouterLink to={`/${item.component}`} className="mapa-subtitle-link">
                                    <DefaultTextBold
                                        text={item.title}
                                        style={{ ...calculateTextStyle(20) }}
                                        className="mapa-title"
                                        sx={sxTextLink}
                                    />
                                </RouterLink>
                        )
                    ) : (
                        <DefaultTextBold
                            text={item.title}
                            style={{ ...calculateTextStyle(20) }}
                            className="mapa-title"
                        />
                    )}
                    {item?.subMenu && item?.subMenu.length > 0 && renderSubMenu(item.subMenu)}
                </div>
            ))}
            <ModalFeedBack
                showModal={showErrorModal}
                handleShowModal={() => setShowErrorModal(false)}
                icon={'error'}
                textButton={''}
                title={'Erro ao carregar informações do usuário.'}
                subTitle={'Tente novamente mais tarde.'}
            />
            <ModalFeedBack
                showModal={showModalRestricted}
                handleShowModal={() => setShowModalRestricted(false)}
                icon={'warning'}
                textButton={''}
                title={'Acesso não permitido'}
                subTitle={modalText}
                classNameModal='modal-custom'
            />
            {modalFeedback && (
                <ModalFeedBack
                    {...modalFeedback}
                />
            )}
        </div>
    );
};

export default MapaDoSite;
